import React, { useState, useEffect } from "react";
import {
    faDollarSign,
    faRoad,
    faGasPump,
    faTools,
    faUserClock,
    faMapMarkedAlt,
    faChartLine,
    faCalendarAlt
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useAuth0 } from "@auth0/auth0-react";
import LaneService from "../../services/LaneService";
import { Spinner } from "react-bootstrap";

const formatCurrency = (value) => `$${value?.toFixed(2) ?? "0.00"}`;
const formatDuration = (minutes) => {
    const hrs = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    return `${hrs}h ${mins}m`;
};

const RouteSummary = ({ routeData, stops, selectedVehicle }) => {
    const { getAccessTokenSilently } = useAuth0();
    const [pricingData, setPricingData] = useState({});
    const [isLoadingPricing, setIsLoadingPricing] = useState(false);
    const [pricingError, setPricingError] = useState(null);

    // Helper function to format pricing value or show "not available"
    const formatPricingValue = (averageLoadRate, costPerMile) => {
        if (averageLoadRate !== null && averageLoadRate !== undefined && averageLoadRate !== 0) {
            return `$${Math.round(averageLoadRate)}/load`;
        }
        if (costPerMile !== null && costPerMile !== undefined && costPerMile !== 0) {
            return `$${costPerMile.toFixed(2)}/mile`;
        }
        return "Price not available";
    };

    const costSummary = routeData?.summary?.costsSummary || {};
    const fuel = costSummary.fuelCost?.value ?? 0.0;
    const maintenance = costSummary.maintenanceCost?.value ?? 0;
    const labor = costSummary.laborCost?.value ?? 0;
    const toll = costSummary.tollCosts?.[0]?.value ?? 0;
    const total = costSummary.totalCost?.[0]?.value ?? fuel + maintenance + labor + toll;

    const distance = routeData?.summary?.length?.value ?? 0;
    const duration = routeData?.summary?.duration?.value ?? 0;

    // Fetch FareLanes pricing data
    useEffect(() => {
        const fetchPricingData = async () => {
            if (!stops || stops.length < 2) return;
            
            const validStops = stops.filter(stop => stop && stop.displayName);
            if (validStops.length < 2) return;

            setIsLoadingPricing(true);
            setPricingError(null);
            
            try {
                const token = await getAccessTokenSilently();
                const startStop = validStops[0];
                const endStop = validStops[validStops.length - 1];
                
                // Use default values for equipment and radius if not available
                const equipment = "Dry Van";
                const pickRadius = 25; // Default radius
                const dropRadius = 25; // Default radius
                
                // Use 30 days as the API call parameter - the response will contain all time periods
                const days = 30;
                
                // State abbreviation to full name mapping
                const stateMapping = {
                    'AL': 'Alabama', 'AK': 'Alaska', 'AZ': 'Arizona', 'AR': 'Arkansas', 'CA': 'California',
                    'CO': 'Colorado', 'CT': 'Connecticut', 'DE': 'Delaware', 'FL': 'Florida', 'GA': 'Georgia',
                    'HI': 'Hawaii', 'ID': 'Idaho', 'IL': 'Illinois', 'IN': 'Indiana', 'IA': 'Iowa',
                    'KS': 'Kansas', 'KY': 'Kentucky', 'LA': 'Louisiana', 'ME': 'Maine', 'MD': 'Maryland',
                    'MA': 'Massachusetts', 'MI': 'Michigan', 'MN': 'Minnesota', 'MS': 'Mississippi', 'MO': 'Missouri',
                    'MT': 'Montana', 'NE': 'Nebraska', 'NV': 'Nevada', 'NH': 'New Hampshire', 'NJ': 'New Jersey',
                    'NM': 'New Mexico', 'NY': 'New York', 'NC': 'North Carolina', 'ND': 'North Dakota', 'OH': 'Ohio',
                    'OK': 'Oklahoma', 'OR': 'Oregon', 'PA': 'Pennsylvania', 'RI': 'Rhode Island', 'SC': 'South Carolina',
                    'SD': 'South Dakota', 'TN': 'Tennessee', 'TX': 'Texas', 'UT': 'Utah', 'VT': 'Vermont',
                    'VA': 'Virginia', 'WA': 'Washington', 'WV': 'West Virginia', 'WI': 'Wisconsin', 'WY': 'Wyoming'
                };

                // Extract city and state names properly
                const pickCity = startStop.city || startStop.displayName;
                const pickStateAbbr = startStop.stateName || startStop.address?.stateCode || startStop.state;
                const pickState = stateMapping[pickStateAbbr] || pickStateAbbr;
                
                const dropCity = endStop.city || endStop.displayName;
                const dropStateAbbr = endStop.stateName || endStop.address?.stateCode || endStop.state;
                const dropState = stateMapping[dropStateAbbr] || dropStateAbbr;
                
                console.log("Pricing API Parameters:", {
                    pickCity,
                    pickState,
                    dropCity,
                    dropState,
                    equipment,
                    pickRadius,
                    dropRadius,
                    days
                });
                
                console.log("Start Stop Object:", startStop);
                console.log("End Stop Object:", endStop);
                
                // Try with URL encoding
                const encodedPickCity = encodeURIComponent(pickCity);
                const encodedPickState = encodeURIComponent(pickState);
                const encodedDropCity = encodeURIComponent(dropCity);
                const encodedDropState = encodeURIComponent(dropState);
                const encodedEquipment = encodeURIComponent(equipment);
                
                console.log("Encoded Parameters:", {
                    encodedPickCity,
                    encodedPickState,
                    encodedDropCity,
                    encodedDropState,
                    encodedEquipment
                });
                
                // Get coordinates from stops
                const pickCityLat = startStop.coordinates?.lat;
                const pickCityLng = startStop.coordinates?.lon;
                const dropCityLat = endStop.coordinates?.lat;
                const dropCityLng = endStop.coordinates?.lon;
                
                if (!pickCityLat || !pickCityLng || !dropCityLat || !dropCityLng) {
                    throw new Error("Missing coordinates for pricing calculation");
                }
                
                const response = await LaneService.getLaneStatisticsByLatLng(
                    pickCityLat,
                    pickCityLng,
                    dropCityLat,
                    dropCityLng,
                    equipment,
                    pickRadius,
                    dropRadius,
                    token
                );
                
                console.log("Pricing API Response:", response.data);
                setPricingData(response.data);
            } catch (error) {
                console.error("Failed to fetch pricing data:", error);
                setPricingError("Failed to load pricing data");
            } finally {
                setIsLoadingPricing(false);
            }
        };

        fetchPricingData();
    }, [stops, selectedVehicle, getAccessTokenSilently]);

    return (
    	<>

    	{/* Left Column - Route Summary */}
            
        <div className="col-12 col-sm-3"><div className="rounded-border">

        	<h5>Route Summary</h5>

            <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
            	<ul>
                    <SummaryItem icon={faDollarSign} label="Total Cost" value={formatCurrency(total)} />
                    <SummaryItem icon={faRoad} label="Toll Cost" value={formatCurrency(toll)} />
                    <SummaryItem icon={faGasPump} label="Fuel Cost" value={formatCurrency(fuel)} />
                    <SummaryItem icon={faTools} label="Maintenance" value={formatCurrency(maintenance)} />
                    <SummaryItem icon={faUserClock} label="Labor Cost" value={formatCurrency(labor)} />
                    <SummaryItem icon={faMapMarkedAlt} label="Distance" value={`${distance.toFixed(2)} mi`} />
                    <SummaryItem icon={faUserClock} label="Duration" value={formatDuration(duration)} />
                </ul>
            </div>
                
        </div></div>

        <div className="col-12 col-sm-5"><div className="rounded-border">
        
        	<h5>FareLanes Market Pricing</h5>
                    
        	{isLoadingPricing && (
            	<div style={{ 
                            display: "flex", 
                            alignItems: "center", 
                            gap: "8px",
                            fontSize: "12px",
                            color: "#6c757d"
                        }}>
                            <Spinner size="sm" />
                            <span>Loading market rates...</span>
                        </div>
                    )}
                    
                    {pricingError && (
                        <div style={{ fontSize: "12px", color: "#dc3545" }}>
                            {pricingError}
                        </div>
                    )}
                    
                    {pricingData && !isLoadingPricing && (
                        // <div style={{ 
                        //     display: "flex", 
                        //     flexDirection: "column", 
                        //     gap: "12px"
                        // }}>
                	<ul>
                            {/* New API Fields from lane-statistics-lat-lng */}
                            {pricingData.queryCurrentStatistic && (
                                <>
                                    {/* Cost per Mile */}
                                    <SummaryItem 
                                        icon={faChartLine} 
                                        label="Cost per Mile" 
                                        value={formatPricingValue(null, pricingData.queryCurrentStatistic?.costPerMile)} 
                                    />
                                    
                                    {/* Cost per Load */}
                                    <SummaryItem 
                                        icon={faDollarSign} 
                                        label="Cost per Load" 
                                        value={formatPricingValue(pricingData.queryCurrentStatistic?.averageLoadRate, null)} 
                                    />
                                    
                                    {/* Price Range */}
                                    <SummaryItem 
                                        icon={faChartLine} 
                                        label="Price Range" 
                                        value={
                                            (pricingData.queryCurrentStatistic?.low && pricingData.queryCurrentStatistic?.high) 
                                                ? `$${pricingData.queryCurrentStatistic.low?.toFixed(0)} - $${pricingData.queryCurrentStatistic.high?.toFixed(0)}`
                                                : "Price not available"
                                        } 
                                    />
                                    
                                    {/* Average Load Distance */}
                                    <SummaryItem 
                                        icon={faRoad} 
                                        label="Avg. Load Distance" 
                                        value={
                                            pricingData.queryCurrentStatistic?.averageLoadMiles 
                                                ? `${pricingData.queryCurrentStatistic.averageLoadMiles?.toFixed(0)} miles`
                                                : "Distance not available"
                                        } 
                                    />
                                </>
                            )}
                            
                            {/* Keep existing fields for backward compatibility */}
                            {pricingData.thirtyDaysAverage && (
                                <SummaryItem 
                                    icon={faCalendarAlt} 
                                    label="4 Months" 
                                    value={`$${Math.round(pricingData.thirtyDaysAverage?.averageLoadRate || 0)}/load, $${(pricingData.thirtyDaysAverage?.costPerMile || 0).toFixed(2)}/mile`} 
                                />
                            )}
                            
                            {pricingData.fifteenDaysAverage && (
                                <SummaryItem 
                                    icon={faCalendarAlt} 
                                    label="2 Months" 
                                    value={`$${Math.round(pricingData.fifteenDaysAverage?.averageLoadRate || 0)}/load, $${(pricingData.fifteenDaysAverage?.costPerMile || 0).toFixed(2)}/mile`} 
                                />
                            )}
                            
                            {pricingData.sevenDaysAverage && (
                                <SummaryItem 
                                    icon={faCalendarAlt} 
                                    label="30 Days" 
                                    value={`$${Math.round(pricingData.sevenDaysAverage?.averageLoadRate || 0)}/load, $${(pricingData.sevenDaysAverage?.costPerMile || 0).toFixed(2)}/mile`} 
                                />
                            )}
                            
                            {pricingData.threeDaysAverage && (
                                <SummaryItem 
                                    icon={faCalendarAlt} 
                                    label="7 Days" 
                                    value={`$${Math.round(pricingData.threeDaysAverage?.averageLoadRate || 0)}/load, $${(pricingData.threeDaysAverage?.costPerMile || 0).toFixed(2)}/mile`} 
                                />
                            )}
                            
                            {pricingData.oneDaysAverage && (
                                <SummaryItem 
                                    icon={faCalendarAlt} 
                                    label="3 Days" 
                                    value={`$${Math.round(pricingData.oneDaysAverage?.averageLoadRate || 0)}/load, $${(pricingData.oneDaysAverage?.costPerMile || 0).toFixed(2)}/mile`} 
                                />
                            )}
                                </ul>
                        // </div>
                    )}
        	</div></div>
        </>
    );
};

const SummaryItem = ({ icon, label, value }) => (

	<li>
        <FontAwesomeIcon icon={icon}/>
        <strong>{label}:</strong>
        <span>{value}</span>
	</li>
);

export default RouteSummary;